<template>
  <div ref="rootRef" class="relative w-full sm:w-64 md:w-80 max-w-md">
    <div class="relative">
      <Icon
        icon="mdi:magnify"
        class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none"
      />
      <input
        ref="inputRef"
        v-model="query"
        type="search"
        :placeholder="t('search.placeholder')"
        class="w-full h-9 pl-9 pr-16 text-sm rounded-lg border border-stone-300 dark:border-amber-500/40 bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm text-stone-800 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60 dark:focus:ring-amber-500/50"
        @focus="open = true"
        @keydown.escape="closeSearch"
        @keydown.down.prevent="moveHighlight(1)"
        @keydown.up.prevent="moveHighlight(-1)"
        @keydown.enter.prevent="selectHighlighted"
      />
      <kbd
        class="hidden sm:inline-flex absolute right-2 top-1/2 -translate-y-1/2 items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-stone-400 dark:text-stone-500 border border-stone-300 dark:border-stone-600 rounded"
      >
        Ctrl K
      </kbd>
    </div>

    <transition name="search-drop">
      <div
        v-if="open && (query.trim() || loading)"
        class="absolute top-full left-0 right-0 mt-1 z-[60] rounded-lg border border-stone-300 dark:border-amber-500/30 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md shadow-xl overflow-hidden"
      >
        <div
          v-if="loading && !ready"
          class="px-4 py-3 text-sm text-stone-500 dark:text-stone-400"
        >
          {{ t("search.loading") }}
        </div>

        <div
          v-else-if="query.trim() && results.length === 0"
          class="px-4 py-3 text-sm text-stone-500 dark:text-stone-400"
        >
          {{ t("search.noResults", { query }) }}
        </div>

        <ul v-else-if="results.length" class="max-h-72 overflow-y-auto py-1">
          <li
            v-for="(result, index) in results"
            :key="result.id"
            @mouseenter="highlightedIndex = index"
            @click="navigateTo(result)"
            :class="[
              'px-3 py-2 cursor-pointer flex items-start gap-3 transition-colors',
              highlightedIndex === index
                ? 'bg-amber-100/80 dark:bg-amber-500/20'
                : 'hover:bg-stone-100 dark:hover:bg-stone-800/60',
            ]"
          >
            <span
              :class="categoryColor(result.category)"
              class="mt-0.5 shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded"
            >
              {{ t(`search.categories.${result.category}`) }}
            </span>
            <div class="min-w-0">
              <p class="text-sm font-medium text-stone-800 dark:text-stone-100 truncate">
                {{ result.title }}
              </p>
              <p class="text-xs text-stone-500 dark:text-stone-400 truncate">
                {{ result.excerpt }}
              </p>
            </div>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";
import { useSearchIndex, type SearchResult } from "@/composables/useSearchIndex";

const router = useRouter();
const { t } = useI18n();
const { query, results, loading, ready, initSearchIndex } = useSearchIndex();

const open = ref(false);
const highlightedIndex = ref(0);
const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

const categoryColor = (category: SearchResult["category"]): string => {
  const map: Record<SearchResult["category"], string> = {
    Page: "bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300",
    Profile: "bg-amber-200/80 dark:bg-amber-500/30 text-amber-800 dark:text-amber-200",
    Blog: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    Project: "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300",
    Resume: "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300",
  };
  return map[category];
};

const navigateTo = (result: SearchResult): void => {
  const path = result.anchor ? `${result.route}#${result.anchor}` : result.route;
  router.push(path);
  closeSearch();
};

const closeSearch = (): void => {
  open.value = false;
  query.value = "";
  highlightedIndex.value = 0;
};

const moveHighlight = (delta: number): void => {
  if (!results.value.length) return;
  highlightedIndex.value =
    (highlightedIndex.value + delta + results.value.length) % results.value.length;
};

const selectHighlighted = (): void => {
  const result = results.value[highlightedIndex.value];
  if (result) navigateTo(result);
};

const handleClickOutside = (e: MouseEvent): void => {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) {
    open.value = false;
  }
};

const handleGlobalKey = (e: KeyboardEvent): void => {
  if ((e.ctrlKey || e.metaKey) && e.key === "k") {
    e.preventDefault();
    inputRef.value?.focus();
    open.value = true;
  }
};

watch(query, () => {
  highlightedIndex.value = 0;
  if (query.value.trim()) open.value = true;
});

onMounted(() => {
  initSearchIndex();
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleGlobalKey);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleGlobalKey);
});
</script>

<style scoped>
.search-drop-enter-active,
.search-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.search-drop-enter-from,
.search-drop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
